import { installedItems } from "@p0security/shared/install/installed";
import { integrationLabel } from "@p0security/shared/integrations/registry";
import type { PermissionType } from "@p0security/shared/types/permission";
import { sortBy } from "lodash";

import type { FrontendInstallContext } from "./types";

/**
 * Intended to be used as the optionProvider for a dynamic field of a `FrontendInstaller`, this function enumerates
 * available installations for one or more integrations and returns a list of options. For example, if an integration
 * component has a dynamic field so that the user can select an AWS account or GCP project, this function can be used.
 *
 * @param existingComponent The component for the item currently being installed, to identify the existing selection of
 * the dynamic field if any. For example, if this is being used as the optionProvider for a dynamic field in the
 * `"access-management"` component of an integration, this should be `"access-management"`.
 * @param integrations An array of objects describing the integrations to list installations for. Each object contains
 * the following properties:
 * - `component`: The component of the integration to list installations for. E.g., for AWS and GCP, this would likely
 *   be `"iam-write"`.
 * - `integration`: The integration to list installations for.
 * - `keyPrefix`: An optional prefix to prepend to the identifying key of each installation, with a colon automatically
 *   inserted after the prefix. This can be useful in cases where multiple integrations are listed in the same dynamic
 *   field (e.g., AWS and GCP), to distinguish between those integrations. **Caution: May cause unexpected behavior if
 *   changed after the integration has been installed by someone.** For example, an existing selection would likely
 *   no longer be detected.
 */
export const integrationOptionProvider = (
  existingComponent: string,
  integrations: {
    component: string;
    integration: PermissionType;
    keyPrefix?: string;
  }[]
) => {
  const toLabel = (title: string, id: string, item?: { label?: string }) =>
    `${title}: ${item?.label ? `${item.label} (${id})` : id}`;

  return async (context: FrontendInstallContext<any>) => {
    const { config, installed } = context;
    const existingWrite = config
      ? installedItems(existingComponent, config)
      : {};
    const options: {
      id: string;
      label?: string;
    }[] = [];

    for (const spec of integrations) {
      const { component, integration, keyPrefix } = spec;
      const data = installed[integration];
      if (!data) continue;

      const allItems = installedItems(component, data);
      for (const [id, item] of Object.entries(allItems)) {
        const key = keyPrefix ? `${keyPrefix}:${id}` : id;
        if (key in existingWrite) {
          continue;
        }

        options.push({
          id: key,
          label: toLabel(integrationLabel(integration), id, item),
        });
      }
    }

    return sortBy(options, (options) => options.label ?? options.id);
  };
};
